/**
 * Meta Conversions API (CAPI) helper.
 *
 * Fires the same event to BOTH the Meta Pixel (browser) AND our backend proxy
 * using a shared eventID so Meta can deduplicate them. All hashing + token
 * usage happens server-side — no secrets ever ship in the frontend bundle.
 */

const PIXEL_ID = '1170270748358781';
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Read Meta cookies for match quality (set automatically by fbq)
const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : null;
};

/**
 * Fire a Meta event on both Pixel and CAPI with a shared event ID.
 * @param {Object} opts
 * @param {'PageView'|'ViewContent'|'Lead'|'Schedule'|'Purchase'|'Contact'} opts.eventName
 * @param {Object} [opts.userData] { email, phone, first_name, last_name, external_id }
 * @param {Object} [opts.customData] { value, currency, content_name, ... }
 * @returns {Promise<string>} the eventID used
 */
export async function trackMetaEvent({ eventName, userData = {}, customData = {} } = {}) {
  const eventId =
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  const eventSourceUrl = typeof window !== 'undefined' ? window.location.href : '';

  // 1) Client-side Pixel event with eventID for dedup
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      window.fbq('trackSingle', PIXEL_ID, eventName, customData, { eventID: eventId });
    } catch (_) {
      // Never let tracking failures block the UX
    }
  }

  // 2) Server-side CAPI event via our backend proxy (token stays on the server)
  try {
    await fetch(`${API_BASE}/api/meta/capi`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: eventSourceUrl,
        user_data: {
          ...userData,
          fbp: getCookie('_fbp'),
          fbc: getCookie('_fbc'),
        },
        custom_data: customData,
      }),
      keepalive: true,
    });
  } catch (_) {
    // Swallow — event failure never breaks the flow
  }

  return eventId;
}
